import { useState, useEffect, useCallback } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, Badge, SectionTitle, RefreshBtn } from "../primitives";

export const HarveySettings = () => {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [vaultProjects, setVaultProjects] = useState<any>(null);
  const [newVaultName, setNewVaultName] = useState("");
  const [creatingVault, setCreatingVault] = useState(false);

  const load = useCallback(async () => {
    const data = await apiCall('/settings/harvey');
    if (data) setConfig(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    const r = await apiCall('/settings/harvey/test', { method: 'POST' });
    setTestResult(r); setTesting(false);
  };

  const toggleEnabled = async (enabled: boolean) => {
    await apiCall('/settings/harvey', { method: 'PUT', body: JSON.stringify({ enabled }) });
    load();
  };

  const updateModel = async (model: string) => {
    await apiCall('/settings/harvey', { method: 'PUT', body: JSON.stringify({ default_model: model }) });
    load();
  };

  const listVault = async () => {
    const r = await apiCall('/settings/harvey/vault');
    if (r) setVaultProjects(r.projects || r.data || []);
  };

  const createVault = async () => {
    if (!newVaultName.trim()) return;
    setCreatingVault(true);
    const r = await apiCall('/settings/harvey/vault/create', { method: 'POST', body: JSON.stringify({ name: newVaultName.trim() }) });
    setCreatingVault(false);
    if (r?.project_id) { setNewVaultName(""); load(); listVault(); }
  };

  if (loading) return <div style={{ padding: 20, color: T.txM }}>Loading Harvey AI configuration...</div>;

  const sc = config?.connectionStatus === 'connected' ? { c: T.grn, b: T.grnS } : config?.connectionStatus === 'error' ? { c: T.red, b: T.redS } : { c: T.txD, b: T.bg4 };

  return (
    <div>
      <SectionTitle sub="Harvey AI integration for contract analysis" right={<RefreshBtn onClick={() => { load(); }} />}>Harvey AI</SectionTitle>

      {/* Connection Status */}
      <Card style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>Connection</div>
            <Badge c={sc.c} b={sc.b}>{config?.connectionStatus === 'connected' ? 'Connected' : config?.connectionStatus === 'error' ? 'Error' : 'Not Configured'}</Badge>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn v={config?.enabled ? 'success' : 'secondary'} sz="sm" onClick={() => toggleEnabled(!config?.enabled)}>{config?.enabled ? 'Enabled' : 'Disabled'}</Btn>
            <Btn v="secondary" sz="sm" onClick={() => { testConnection(); }} disabled={testing || !config?.configured}>{testing ? 'Testing...' : 'Test Connection'}</Btn>
          </div>
        </div>
        {!config?.configured && <div style={{ fontSize: 11, color: T.txD, padding: '8px 10px', backgroundColor: T.bg3, borderRadius: 4 }}>The Harvey API key is configured via the server environment file (.env). Contact your system administrator to add or update the HARVEY_API_KEY.</div>}
        {config?.accountInfo && !config.accountInfo.error && <div style={{ fontSize: 11, color: T.txM, marginTop: 8 }}>Account: {JSON.stringify(config.accountInfo).substring(0, 200)}</div>}
        {config?.accountInfo?.error && <div style={{ fontSize: 11, color: T.red, marginTop: 8 }}>Error: {config.accountInfo.error}</div>}
        {testResult && (
          <div style={{ marginTop: 10, padding: 10, backgroundColor: testResult.success ? T.grnS : T.redS, borderRadius: 4, fontSize: 11, color: testResult.success ? T.grn : T.red }}>
            {testResult.success ? `Connected. Response: "${testResult.testResponse}"` : `Failed: ${testResult.completionError || testResult.error || 'Unknown error'}`}
          </div>
        )}
      </Card>

      {/* Model + Settings */}
      <Card style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 10 }}>Settings</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 4, fontWeight: 700 }}>Default Model</div>
            <select value={config?.defaultModel || ''} onChange={e => updateModel(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, backgroundColor: T.bg3, color: T.tx, fontFamily: 'inherit' }}>
              <option value="">Workspace Default</option>
              <option value="gpt-5">GPT-5</option>
              <option value="gpt-5-1">GPT-5.1</option>
              <option value="claude-opus-4-5">Claude Opus 4.5</option>
              <option value="claude-sonnet-4-5">Claude Sonnet 4.5</option>
              <option value="claude-opus-4-6">Claude Opus 4.6</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 4, fontWeight: 700 }}>API URL</div>
            <div style={{ fontSize: 12, color: T.tx, padding: '8px 10px', backgroundColor: T.bg3, borderRadius: 5, border: `1px solid ${T.brd}` }}>{config?.apiUrl || 'https://api.harvey.ai'}</div>
          </div>
        </div>
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 10, color: T.txD }}>
          <div>Rate limit: 20 completions/min</div>
          <div>Vault: 10 ops/min</div>
        </div>
      </Card>

      {/* Vault */}
      <Card style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Vault (Knowledge Base)</div>
          <Btn v="secondary" sz="sm" onClick={() => { listVault(); }} disabled={!config?.configured}>List Projects</Btn>
        </div>
        {config?.vaultProjectId && <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>Current project: <code style={{ color: T.acc }}>{config.vaultProjectId}</code></div>}
        {vaultProjects && (
          <div style={{ marginBottom: 10 }}>
            {vaultProjects.length === 0 ? <div style={{ fontSize: 11, color: T.txD }}>No vault projects found</div> : vaultProjects.map((p: any, i: number) => (
              <div key={i} style={{ padding: '6px 10px', backgroundColor: T.bg3, borderRadius: 4, marginBottom: 4, fontSize: 11, color: T.tx }}>{p.name || p.project_name || JSON.stringify(p).substring(0, 100)}</div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={newVaultName} onChange={e => setNewVaultName(e.target.value)} placeholder="New project name..." style={{ flex: 1, padding: '6px 10px', border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, backgroundColor: T.bg3, color: T.tx, fontFamily: 'inherit' }} />
          <Btn v="primary" sz="sm" onClick={() => { createVault(); }} disabled={creatingVault || !newVaultName.trim()}>{creatingVault ? 'Creating...' : 'Create Project'}</Btn>
        </div>
      </Card>
    </div>
  );
};
